import { useMemo } from "react";
import { motion, useReducedMotion, useTime, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import icons
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
// import images
import spaceImage from "../../image/astral-wallpapers.jpg";
import floatingCow from "../../image/there-is-cow-space-suit-floating.png";
// import components
import { ImageBannerFrame } from "../display/Frame";
// import context
import { useLang } from "../Context";
// import global var
import { typedVocab } from "../GlobalVar";

const COW_IMG_CLASS =
    "absolute right-[2%] top-[10%] w-[min(72vw,360px)] opacity-95 sm:right-[4%] sm:top-[12%] sm:w-[min(58vw,440px)] lg:right-[6%] lg:top-[14%] lg:w-[min(48vw,520px)] lg:max-w-[520px]";

interface MotionWave {
    freq: number;
    amp: number;
    phase: number;
}

interface ZeroGProfile {
    baseRotate: number;
    xWaves: MotionWave[];
    yWaves: MotionWave[];
    rotateWaves: MotionWave[];
    scaleWaves: MotionWave[];
    /** Very slow signed drift — small rate so tumble rarely completes full circles */
    rotateDrift: { rate: number; phase: number };
    amplitudeScale: number;
}

function rand(min: number, max: number) {
    return min + Math.random() * (max - min);
}

function motionWave(
    freqMin: number,
    freqMax: number,
    ampMin: number,
    ampMax: number
): MotionWave {
    return {
        freq: rand(freqMin, freqMax),
        amp: rand(ampMin, ampMax),
        phase: rand(0, Math.PI * 2),
    };
}

/** Per page load — incommensurate frequencies + random phase so paths rarely repeat */
function createZeroGProfile(amplitudeScale = 1): ZeroGProfile {
    const s = amplitudeScale;
    const wave = (
        freqMin: number,
        freqMax: number,
        ampMin: number,
        ampMax: number
    ) => motionWave(freqMin, freqMax, ampMin * s, ampMax * s);

    return {
        baseRotate: rand(-28, 38) * Math.min(s + 0.35, 1.2),
        xWaves: [
            wave(0.11, 0.38, 58, 98),
            wave(0.07, 0.21, 32, 58),
            wave(0.04, 0.13, 18, 38),
        ],
        yWaves: [
            wave(0.09, 0.33, 48, 82),
            wave(0.06, 0.18, 30, 52),
            wave(0.035, 0.1, 14, 34),
        ],
        rotateWaves: [
            wave(0.05, 0.17, 38, 68),
            wave(0.08, 0.23, 28, 55),
            wave(0.1, 0.29, 22, 48),
            wave(0.13, 0.37, 18, 40),
            wave(0.016, 0.048, 12, 32),
        ],
        scaleWaves: [
            wave(0.04, 0.11, 0.018, 0.038),
            wave(0.025, 0.075, 0.01, 0.028),
        ],
        rotateDrift: {
            rate: rand(-0.9, 0.9) * s,
            phase: rand(0, Math.PI * 2),
        },
        amplitudeScale: s,
    };
}

function sumSinWaves(s: number, waves: MotionWave[]) {
    return waves.reduce(
        (acc, w) => acc + Math.sin(s * w.freq + w.phase) * w.amp,
        0
    );
}

function sumCosWaves(s: number, waves: MotionWave[]) {
    return waves.reduce(
        (acc, w) => acc + Math.cos(s * w.freq + w.phase) * w.amp,
        0
    );
}

/**
 * Continuous sine-driven drift — smooth velocity, no keyframe corners / loop snap.
 * Random profile per visit; no constant spin rate (avoids repeating full circles).
 */
function useZeroGMotion(amplitudeScale = 1) {
    const profile = useMemo(
        () => createZeroGProfile(amplitudeScale),
        [amplitudeScale]
    );
    const time = useTime();

    const x = useTransform(time, (t) => sumSinWaves(t / 1000, profile.xWaves));

    const y = useTransform(time, (t) => sumCosWaves(t / 1000, profile.yWaves));

    const rotate = useTransform(time, (t) => {
        const s = t / 1000;
        const tumble = sumSinWaves(s, profile.rotateWaves);
        const drift =
            profile.rotateDrift.rate * s +
            Math.sin(s * 0.027 + profile.rotateDrift.phase) *
                14 * profile.amplitudeScale;
        return profile.baseRotate + tumble + drift;
    });

    const scale = useTransform(time, (t) => {
        const sec = t / 1000;
        return 1 + sumSinWaves(sec, profile.scaleWaves);
    });

    return { x, y, rotate, scale, baseRotate: profile.baseRotate };
}

function ZeroGMotionCow({
    className,
    amplitudeScale = 1,
}: {
    className: string;
    amplitudeScale?: number;
}) {
    const reduceMotion = useReducedMotion();
    const { x, y, rotate, scale, baseRotate } = useZeroGMotion(amplitudeScale);

    if (reduceMotion) {
        return (
            <img
                src={floatingCow}
                alt=""
                aria-hidden
                className={className}
                style={{
                    transform: `rotate(${baseRotate}deg)`,
                    transformOrigin: "center center",
                }}
            />
        );
    }

    return (
        <motion.img
            src={floatingCow}
            alt=""
            aria-hidden
            className={className}
            style={{
                x,
                y,
                rotate,
                scale,
                transformOrigin: "center center",
            }}
        />
    );
}

function FloatingSpaceCow() {
    return (
        <div
            className="pointer-events-none absolute inset-0 z-20 overflow-visible"
            aria-hidden
        >
            <ZeroGMotionCow className={COW_IMG_CLASS} amplitudeScale={1} />
        </div>
    );
}

export default function NoContent() {
    const { lang } = useLang();

    return (
        <ImageBannerFrame image={spaceImage} disableContentMotion>
            <FloatingSpaceCow />
            <div className="relative z-30 m-auto w-full px-8 sm:w-[75%] lg:w-full lg:px-0">
                <div className="mb-10">
                    <p className="text-left text-2xl font-bold text-white sm:leading-none">
                        {typedVocab["No Content 1"][lang]}
                    </p>
                    <p className="text-left text-lg text-white sm:mt-2">
                        {typedVocab["No Content 2"][lang]}
                    </p>
                </div>
                <div className="w-fit">
                    <Link
                        to={
                            lang === "Zh"
                                ? "/tw"
                                : lang === "Jp"
                                  ? "/jp"
                                  : "/"
                        }
                        className="group flex items-center justify-center gap-2 rounded-md px-2 py-1 text-base text-teal-500 transition-all duration-300 hover:gap-4 hover:bg-teal-500"
                    >
                        <p className="font-bold text-white">
                            {typedVocab["Back to Home"][lang]}
                        </p>
                        <FontAwesomeIcon className="text-white" icon={faArrowRight} />
                    </Link>
                </div>
            </div>
        </ImageBannerFrame>
    );
}

export function LoadingContent() {
    return (
        <div className="flex h-screen w-screen items-center justify-center bg-grey-900">
            <div className="flex h-fit items-center justify-center">
                <p className="text-2xl font-semibold text-white">L</p>
                <div className="relative h-8 w-8">
                    <div className="absolute z-50 m-1.5 h-5 w-5 rounded-full bg-grey-900" />
                    <motion.div
                        className="absolute z-30 h-full w-full rounded-full border-[5px] border-t-transparent border-white"
                        animate={{ rotate: 360 }}
                        transition={{
                            duration: 1,
                            repeat: Infinity,
                            ease: "linear",
                        }}
                    />
                    <div className="absolute z-20 h-full w-full rounded-full bg-grey-900/80" />
                    <div className="absolute z-10 h-full w-full rounded-full bg-white" />
                </div>
                <p className="text-2xl font-semibold text-white">ADING</p>
            </div>
        </div>
    );
}
